import { useEffect, useMemo, useRef } from 'react';

import type { SelectionList } from '@revelio/iso-utility';

import { resolveSelectionListItemLabel } from '../../components/custom-filters/data-builder-selection-list-mapping';
import { useDataBuilderSelectionLists } from '../../components/custom-filters/useDataBuilderSelectionLists';
import type {
  CustomFilter,
  DataBuilderCustomColumn,
} from '../../data-builder.store';
import type {
  CustomFilterDefinition,
  SelectionListFilterDefinition,
} from '../../datasets/deliverable-column-config';

function resolveConditionLabels(
  filter: CustomFilter,
  selectionLists: SelectionList[]
): CustomFilter | null {
  let changed = false;

  const conditions = filter.conditions.map((condition) => {
    if (
      condition.type !== 'selection_list' ||
      !condition.selectionListItems?.length
    ) {
      return condition;
    }

    const hasUnresolved = condition.selectionListItems.some(
      (item) => item.label === item.id
    );
    if (!hasUnresolved) return condition;

    const resolvedItems = condition.selectionListItems.map((item) => {
      if (item.label !== item.id) return item;

      const label = resolveSelectionListItemLabel(
        item.id,
        item.selectionListId,
        selectionLists
      );
      if (label && label !== item.label) {
        changed = true;
        return { ...item, label };
      }
      return item;
    });

    return { ...condition, selectionListItems: resolvedItems };
  });

  return changed ? { ...filter, conditions } : null;
}

function resolveCustomColumnLabels(
  column: DataBuilderCustomColumn,
  selectionLists: SelectionList[]
): DataBuilderCustomColumn | null {
  let changed = false;

  const steps = column.steps.map((step) => {
    if (step.case === null) return step;
    const resolved = resolveConditionLabels(step.case, selectionLists);
    if (resolved) {
      changed = true;
      return { ...step, case: resolved };
    }
    return step;
  });

  return changed ? { ...column, steps } : null;
}

function getSelectionListKeys(
  filterDefinitions: CustomFilterDefinition[]
): string[] {
  return filterDefinitions
    .filter(
      (def): def is SelectionListFilterDefinition =>
        def.type === 'selection_list'
    )
    .flatMap((def) => def.keys);
}

/**
 * Hook that resolves ID-only labels in selection-list filter conditions
 * back to human-readable labels as selection lists load.
 * Runs until all labels are resolved — selection lists for different types
 * (geo, role, skill) may load at different times.
 */
export function useResolveFilterLabels({
  filters,
  filterDefinitions,
  onUpdateFilter,
  customColumns,
  onUpdateCustomColumn,
}: {
  filters?: CustomFilter[];
  filterDefinitions: CustomFilterDefinition[];
  onUpdateFilter?: (index: number, filter: CustomFilter) => void;
  customColumns?: DataBuilderCustomColumn[];
  onUpdateCustomColumn?: (
    index: number,
    column: DataBuilderCustomColumn
  ) => void;
}) {
  const apiKeys = useMemo(
    () => getSelectionListKeys(filterDefinitions),
    [filterDefinitions]
  );
  const { selectionLists } = useDataBuilderSelectionLists({ apiKeys });
  const hasResolvedRef = useRef(false);

  useEffect(() => {
    if (hasResolvedRef.current || selectionLists.length === 0) return;

    const hasUnresolvedFilters = filters?.some((f) =>
      f.conditions.some(
        (c) =>
          c.type === 'selection_list' &&
          c.selectionListItems?.some((item) => item.label === item.id)
      )
    );
    const hasUnresolvedColumns = customColumns?.some((col) =>
      col.steps.some((step) =>
        step.case?.conditions.some(
          (c) =>
            c.type === 'selection_list' &&
            c.selectionListItems?.some((item) => item.label === item.id)
        )
      )
    );

    if (!hasUnresolvedFilters && !hasUnresolvedColumns) {
      hasResolvedRef.current = true;
      return;
    }

    let stillUnresolved = false;

    const hasUnresolvedConditions = (conditions: CustomFilter['conditions']) =>
      conditions.some(
        (c) =>
          c.type === 'selection_list' &&
          c.selectionListItems?.some((item) => item.label === item.id)
      );

    filters?.forEach((filter, index) => {
      const resolved = resolveConditionLabels(filter, selectionLists);
      if (resolved) {
        onUpdateFilter?.(index, resolved);
        // Check if this filter still has unresolved labels after resolution
        if (
          resolved.conditions.some(
            (c) =>
              c.type === 'selection_list' &&
              c.selectionListItems?.some((item) => item.label === item.id)
          )
        ) {
          stillUnresolved = true;
        }
      }
      // Check the result (or the original if nothing changed) for remaining
      // unresolved labels — the needed list may not have loaded yet.
      if (hasUnresolvedConditions((resolved ?? filter).conditions)) {
        stillUnresolved = true;
      }
    });

    customColumns?.forEach((column, index) => {
      const resolved = resolveCustomColumnLabels(column, selectionLists);
      if (resolved) {
        onUpdateCustomColumn?.(index, resolved);
      }
      const steps = (resolved ?? column).steps;
      if (
        steps.some((step) =>
          step.case ? hasUnresolvedConditions(step.case.conditions) : false
        )
      ) {
        stillUnresolved = true;
      }
    });

    // Only mark done when all labels are resolved — selection lists for
    // different types (geo, role, skill) may load at different times.
    if (!stillUnresolved) {
      hasResolvedRef.current = true;
    }
  }, [
    selectionLists,
    filters,
    onUpdateFilter,
    customColumns,
    onUpdateCustomColumn,
  ]);
}
